<ng-container *ngIf="showSaveButton; else embeddedLayout">
  <ion-header class="ion-no-border">
    <ion-toolbar>
      <ion-title>{{ 'Activity details' | translate }}</ion-title>
    </ion-toolbar>
  </ion-header>

  <ion-content class="ion-padding">
    <ng-container *ngTemplateOutlet="formTpl"></ng-container>
    
    <ion-fab slot="fixed" vertical="bottom" horizontal="start">
      <ion-fab-button color="light" (click)="dismiss()">
        <ion-icon name="close-outline"></ion-icon>
      </ion-fab-button>
    </ion-fab>

    <ion-fab slot="fixed" vertical="bottom" horizontal="end">
      <ion-fab-button (click)="submitForm()">
        <ion-icon name="save-outline"></ion-icon>
      </ion-fab-button>
    </ion-fab>
  </ion-content>
</ng-container>

<ng-template #embeddedLayout>
  <ng-container *ngTemplateOutlet="formTpl"></ng-container>
</ng-template>

<ng-template #formTpl>
  <form [formGroup]="form" (ngSubmit)="submitForm()" class="custom-form">
    <div class="form-group" [class.invalid-group]="isSubmitted && form.get('animals')?.invalid">
      <label class="form-label">{{ 'Animals' | translate }}</label>
      <app-animal-select formControlName="animals" [animals]="allAnimals" [multiple]="true"></app-animal-select>
    </div>

    <div class="form-group">
      <label class="form-label">{{ 'Activity type' | translate }}</label>
      <div class="type-grid">
        <div class="type-option" [class.active]="form.get('type')?.value === type" (click)="form.get('type')?.setValue(type)" *ngFor="let type of getFilteredActivityTypes()">
          <span>{{ type | translate }}</span>
        </div>
      </div>
    </div>

    <div class="form-group" style="margin-bottom: 24px;" *ngIf="isTypeWithStats() && !isFromLiveRecording">
      <ion-segment [(ngModel)]="entryMode" [ngModelOptions]="{standalone: true}" mode="ios">
        <ion-segment-button value="manual">
          <ion-icon name="create-outline"></ion-icon>
          <ion-label>{{ 'Manual' | translate }}</ion-label>
        </ion-segment-button>
        <ion-segment-button value="gpx">
          <ion-icon name="map-outline"></ion-icon>
          <ion-label>{{ 'GPX' | translate }}</ion-label>
        </ion-segment-button>
      </ion-segment>
    </div>

    <div class="recorded-card" *ngIf="recordedData">
      <div class="recorded-header">
        <ion-icon name="checkmark-circle" color="success"></ion-icon>
        <span>{{ 'Session recorded' | translate }}</span>
      </div>
      <div class="recorded-stats">
          <div class="stat">
            <span class="val">{{ recordedData.distance | number:'1.2-2' }} km</span>
            <span class="lbl">{{ 'Distance' | translate }}</span>
          </div>
          <div class="stat">
            <span class="val">{{ formatDurationMinSec(recordedData.duration) }}</span>
            <span class="lbl">{{ 'Duration' | translate }}</span>
          </div>
          <div class="stat">
            <span class="val">{{ recordedData.elevationGain || 0 }} m+</span>
            <span class="lbl">{{ 'Elevation' | translate }}</span>
          </div>
      </div>
    </div>

    <div class="form-group" *ngIf="entryMode === 'manual' && !isFromLiveRecording">
      <label class="form-label">{{ 'Activity date' | translate }}</label>
      <datetime-button [value]="date" (ionChange)="dtbDateActivityChange($event)"></datetime-button>
    </div>

    <!-- Stats Grid (Distance/Duration) - Only for Canicross/CaniVTT -->
    <div class="form-group" *ngIf="isTypeWithStats() && !isFromLiveRecording">
      <!-- Manual Stats Entry -->
      <div class="form-row" *ngIf="entryMode === 'manual' && !isFromLiveRecording">
        <div class="field-item dist-field">
          <label class="form-label">{{ 'Distance (km)' | translate }}</label>
          <ion-input type="number" formControlName="distance" placeholder="0.0" class="custom-input"></ion-input>
        </div>
        <div class="field-item">
          <label class="form-label">{{ 'Elevation (m)' | translate }}</label>
          <ion-input type="number" formControlName="elevationGain" placeholder="0" class="custom-input"></ion-input>
        </div>
        <div class="field-item">
          <label class="form-label">{{ 'Durée (min:sec)' | translate }}</label>
          <div class="form-row duration-row">
              <ion-input type="number" formControlName="durationMinutes" placeholder="0" class="custom-input" (ionFocus)="clearIfZero('durationMinutes')" (ionBlur)="setZeroIfEmpty('durationMinutes')"></ion-input>
              <span class="duration-separator">:</span>
              <ion-input type="number" formControlName="durationSeconds" placeholder="0" class="custom-input" (ionFocus)="clearIfZero('durationSeconds')" (ionBlur)="setZeroIfEmpty('durationSeconds')"></ion-input>
          </div>
        </div>
      </div>

      <!-- GPX Upload Entry -->
      <div class="form-row" *ngIf="entryMode === 'gpx' && !isFromLiveRecording" style="margin-top: 8px;">
        <div class="field-item">
          <label class="form-label">{{ 'GPX File' | translate }}</label>
          <div class="file-upload-container">
            <input type="file" (change)="onFileSelected($event)" accept=".gpx" id="gpx-upload" style="display: none;">
            <ion-button fill="outline" expand="block" onclick="document.getElementById('gpx-upload').click()" class="gpx-btn">
              <ion-icon name="cloud-upload-outline" slot="start"></ion-icon>
              {{ (form.get('gpxFile')?.value?.name || 'Import GPX file') | translate }}
            </ion-button>
          </div>

          <!-- Feedback for GPX extraction -->
          <div class="gpx-feedback" *ngIf="form.get('gpxFile')?.value">
              <div class="feedback-item">
                  <ion-icon name="calendar-outline"></ion-icon>
                  <span>{{ formatDate(form.get('date')?.value) }}</span>
              </div>
              <div class="feedback-item">
                  <ion-icon name="resize-outline"></ion-icon>
                  <span>{{ form.get('distance')?.value }} km</span>
              </div>
              <div class="feedback-item">
                  <ion-icon name="time-outline"></ion-icon>
                  <span>{{ form.get('durationMinutes')?.value }}:{{ form.get('durationSeconds')?.value | number:'2.0-0' }}</span>
              </div>
          </div>
        </div>
      </div>
    </div>

    <div class="form-group">
      <div class="range-field">
        <div class="range-header">
          <label class="form-label" style="margin: 0;">{{ 'Activity difficulty' | translate }} (1-5)</label>
          <span class="range-value" [ngClass]="'v-diff-' + form.value.difficulty">{{ form.value.difficulty }}</span>
        </div>
        <ion-range [min]="1" [max]="5" [snaps]="true" [ticks]="true" formControlName="difficulty" color="warning">
          <ion-icon slot="start" size="small" name="fitness-outline"></ion-icon>
          <ion-icon slot="end" name="fitness"></ion-icon>
        </ion-range>
      </div>
    </div>

    <div class="form-group">
      <div class="range-header">
        <label class="form-label" style="margin: 0;">{{ 'Activity temperature (°C)' | translate }}</label>
        <span class="range-value" 
          [class.v-temp-cold]="form.value.temperature <= 5"
          [class.v-temp-mild]="form.value.temperature > 5 && form.value.temperature <= 20"
          [class.v-temp-warm]="form.value.temperature > 20 && form.value.temperature <= 35"
          [class.v-temp-hot]="form.value.temperature > 35">
          {{ form.value.temperature }}°
        </span>
      </div>
      <ion-range [min]="-20" [max]="50" [snaps]="false" formControlName="temperature" color="danger" (ionChange)="weatherUserModified = true">
        <ion-icon slot="start" size="small" name="thermometer-outline"></ion-icon>
        <ion-icon slot="end" name="thermometer"></ion-icon>
      </ion-range>

      <div class="range-header" style="margin-top: 16px;">
        <label class="form-label" style="margin: 0;">{{ 'Activity humidity' | translate }} (%)</label>
        <span class="range-value" style="background: var(--ion-color-secondary); color: white;">
          {{ form.value.humidity }}%
        </span>
      </div>
      <ion-range [min]="0" [max]="100" [snaps]="false" formControlName="humidity" color="secondary" (ionChange)="weatherUserModified = true">
        <ion-icon slot="start" size="small" name="water-outline"></ion-icon>
        <ion-icon slot="end" name="water"></ion-icon>
      </ion-range>

      <div class="weather-section" style="margin-top: 8px;">
        <div class="form-label">
          <span>{{ 'Activity weather' | translate }}</span>
          <ion-button fill="clear" size="small" (click)="fetchWeather(false)" style="margin: -8px 0; --padding-end: 0;" [title]="'Detect weather from my current location' | translate">
            <ion-icon name="location-outline" slot="start"></ion-icon>
            <span style="font-size: 0.75rem;">{{ 'Auto' | translate }}</span>
          </ion-button>
        </div>
        <div class="weather-grid">
          <div class="weather-option" [class.active]="isWeatherSelected('sun')" (click)="toggleWeather('sun')">
            <ion-icon name="sunny" style="color: #ffce00;"></ion-icon>
            <span>{{ 'Activity sun' | translate }}</span>
          </div>
          <div class="weather-option" [class.active]="isWeatherSelected('shade')" (click)="toggleWeather('shade')">
            <img src="assets/icon/shade.png">
            <span>{{ 'Activity shade' | translate }}</span>
          </div>
          <div class="weather-option" [class.active]="isWeatherSelected('cloudy')" (click)="toggleWeather('cloudy')">
            <ion-icon name="partly-sunny" style="color: #4ec0e2;"></ion-icon>
            <span>{{ 'Activity cloudy' | translate }}</span>
          </div>
          <div class="weather-option" [class.active]="isWeatherSelected('rain')" (click)="toggleWeather('rain')">
            <ion-icon name="rainy-outline" style="color: #3880ff;"></ion-icon>
            <span>{{ 'Activity rain' | translate }}</span>
          </div>
          <div class="weather-option" [class.active]="isWeatherSelected('heavy_rain')" (click)="toggleWeather('heavy_rain')">
            <ion-icon name="thunderstorm" style="color: #3171e0;"></ion-icon>
            <span>{{ 'Activity heavy_rain' | translate }}</span>
          </div>
          <div class="weather-option" [class.active]="isWeatherSelected('snow')" (click)="toggleWeather('snow')">
            <ion-icon name="snow" style="color: #00d4ff;"></ion-icon>
            <span>{{ 'Activity snow' | translate }}</span>
          </div>
          <div class="weather-option" [class.active]="isWeatherSelected('night')" (click)="toggleWeather('night')">
            <ion-icon name="moon" style="color: #616161;"></ion-icon>
            <span>{{ 'Activity night' | translate }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="form-group" *ngIf="isDogSelected()">
      <label class="form-label">{{ 'Activity dog behavior' | translate }}</label>
      <ion-textarea formControlName="dogBehavior" [autoGrow]="true" rows="2" placeholder="{{ 'How was your dog?' | translate }}" class="custom-textarea"></ion-textarea>
    </div>

    <div class="form-group">
      <label class="form-label">{{ 'Animal appreciation' | translate }}</label>
      <div class="rating-selector">
        <div *ngFor="let r of ratings" class="rating-item" 
             [style.color]="r.color"
             [style.filter]="form.get('animalRating')?.value === r.value ? 'none' : 'grayscale(0.8)'"
             [class.active]="form.get('animalRating')?.value === r.value" (click)="setRating(r.value)">
          {{ r.icon }}
        </div>
      </div>
    </div>

    <!-- Gear Card (Harness & Tethered) - Only for Canicross/CaniVTT -->
    <div class="form-group" *ngIf="isTypeWithHarness()">
      <div class="form-row">
        <div class="field-item">
          <label class="form-label">{{ 'Activity harness' | translate }}</label>
          <ion-input type="text" formControlName="harness" placeholder="{{ 'e.g. Non-stop' | translate }}" class="custom-input"></ion-input>
        </div>
        <div class="field-item">
          <label class="form-label">&nbsp;</label>
          <div class="toggle-card">
            <label class="toggle-label" style="font-size: 0.9rem; font-weight: 600;">{{ 'Is the dog tethered?' | translate }}</label>
            <ion-toggle formControlName="isTethered"></ion-toggle>
          </div>
        </div>
      </div>
    </div>

    <div class="form-group">
      <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
        <ion-icon name="eye-outline" color="primary"></ion-icon>
        <label class="form-label" style="margin: 0;">{{ 'Activity visibility' | translate }}</label>
      </div>
      <ion-segment formControlName="visibility" mode="ios">
        <ion-segment-button value="0">
          <ion-label>{{ 'Private' | translate }}</ion-label>
        </ion-segment-button>
        <ion-segment-button value="1">
          <ion-label>{{ 'Friends' | translate }}</ion-label>
        </ion-segment-button>
        <ion-segment-button value="2">
          <ion-label>{{ 'Public' | translate }}</ion-label>
        </ion-segment-button>
      </ion-segment>

      <div style="margin-top: 12px;">
        <label class="form-label">{{ 'Comment' | translate }}</label>
        <ion-textarea formControlName="comment" [autoGrow]="true" rows="3" placeholder="{{ 'Other notes...' | translate }}" class="custom-textarea"></ion-textarea>
      </div>
    </div>
  </form>
</ng-template>
